var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __values = (this && this.__values) || function (o) {
    var m = typeof Symbol === "function" && o[Symbol.iterator], i = 0;
    if (m) return m.call(o);
    return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
};
import { EventEmitter, OnInit, AfterViewInit, Renderer2, ChangeDetectorRef, OnDestroy } from '@angular/core';
import * as Hammer from 'hammerjs';
import { Select, Store } from '@ngxs/store';
import { Subject, Observable } from 'rxjs';
import { takeUntil, filter, take } from 'rxjs/operators';
import { ViewState } from '../../state/view.state';
import { ViewMode } from '../../models/view-mode.enum';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { ViewService } from '../../view.service';
import { ViewToolsMode } from '../../models/view-tools-mode.enum';
var drawerHeight = 64;
var velocityMultipler = 50; // Higher is faster
var triggerVelocityThreshold = 0.2; // Velocity at which drawer is considered 'in motion'. Higher is harder.
var snapVelocity = 0.75; // Velocity at which drawer auto animates. 
var friction = 0.92; // Lower is more aggressive
var toggleSpeed = 0.75; // Speed at which drawer is opened by click
var halfwayPoint = 0.4;
var ViewDrawerComponent = /** @class */ (function () {
    // ---------- Lifecycle ----------
    function ViewDrawerComponent(renderer, changeDetectorRef, viewService, store) {
        this.renderer = renderer;
        this.changeDetectorRef = changeDetectorRef;
        this.viewService = viewService;
        this.store = store;
        // ---------- Input / Output ----------
        this.enablePagination = true;
        this._expandable = true;
        this.offsetYChange = new EventEmitter();
        this.breakpoints = {
            minimumDrawer: {
                velocity: Infinity,
                point: 0
            },
            halfDrawer: {
                velocity: 1,
                point: 0
            },
            fullWindow: {
                velocity: Infinity,
                point: 0
            },
            fullDrawer: {
                velocity: Infinity,
                point: 0
            }
        };
        this.canTouch = false;
        this.ngUnsubscribe = new Subject();
        this.viewMode = ViewMode;
        this.viewToolsMode = ViewToolsMode;
        this.ghostPagination = false;
        this.atMinimum = false;
        this.mc = null;
        this.mode = ViewMode.Content;
        this.hovered = false;
    }
    Object.defineProperty(ViewDrawerComponent.prototype, "expandable", {
        get: function () {
            return this._expandable;
        },
        set: function (value) {
            this._expandable = value;
            (this.expandable) ? this.bindDragEvents() : this.unbindDragEvents();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ViewDrawerComponent.prototype, "fullyOpen", {
        // ---------- Class Variables ----------
        get: function () {
            if (this.canTouch) {
                return this.getDrawerY() <= this.breakpoints.fullWindow.point;
            }
            else {
                return Math.floor(this.getDrawerY()) === Math.floor(this.breakpoints.halfDrawer.point);
            }
        },
        enumerable: true,
        configurable: true
    });
    ViewDrawerComponent.prototype.ngOnInit = function () {
        this.initWindowSizeObserver();
        this.initLayoutObserver();
    };
    ViewDrawerComponent.prototype.ngAfterViewInit = function () {
        this.initInteractions();
        this.refresh(this.store.selectSnapshot(ViewState.getLayout).drawer.isExpandedInitially);
    };
    ViewDrawerComponent.prototype.ngOnDestroy = function () {
        this.unbindDragEvents();
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    };
    // ---------- Layout ----------
    ViewDrawerComponent.prototype.initWindowSizeObserver = function () {
        var _this = this;
        this.renderer.listen('window', 'resize', function () {
            _this.refresh(false);
        });
    };
    ViewDrawerComponent.prototype.initLayoutObserver = function () {
        var _this = this;
        this.layout$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(function (layout) {
            _this.updateMode(layout);
        });
    };
    /**
     * Mobile devices have variable viewheight because the browser bar can scroll in and out of view
     * This workaround determines the available window space and uses that as a viewheight unit
     */
    ViewDrawerComponent.prototype.updateViewHeight = function () {
        var vh = window.innerHeight * 0.01;
        this.viewDrawer.nativeElement.style.setProperty('--vh', vh + "px");
    };
    ViewDrawerComponent.prototype.updateStops = function () {
        var windowHeight = window.innerHeight;
        this.breakpoints.minimumDrawer.point = -drawerHeight;
        this.breakpoints.halfDrawer.point = Math.floor(-(windowHeight * halfwayPoint));
        this.breakpoints.fullWindow.point = -windowHeight;
        this.breakpoints.fullDrawer.point = -this.viewDrawer.nativeElement.offsetHeight;
    };
    ViewDrawerComponent.prototype.updateMode = function (layout) {
        this.mode = this.viewService.inferDrawerMode(layout);
        if (this.mode === ViewMode.Tools) {
            this.toggleDrawer(false);
        }
        this.changeDetectorRef.markForCheck();
    };
    ViewDrawerComponent.prototype.resetDrawerPosition = function (expanded) {
        var _this = this;
        setTimeout(function () {
            var point = expanded
                ? _this.breakpoints.halfDrawer.point
                : _this.breakpoints.minimumDrawer.point;
            _this.flyTo(point);
        });
    };
    ViewDrawerComponent.prototype.refresh = function (expanded) {
        var _this = this;
        setTimeout(function () {
            _this.updateViewHeight();
            _this.updateStops();
            _this.resetDrawerPosition(expanded);
        });
    };
    // ---------- Interaction ----------
    ViewDrawerComponent.prototype.initInteractions = function () {
        var _this = this;
        this.canTouch$.pipe(filter(function (canTouch) { return canTouch; }), take(1)).subscribe(function () {
            _this.canTouch = true;
            _this.changeDetectorRef.markForCheck();
            if (_this.expandable) {
                _this.bindDragEvents();
            }
        });
    };
    ViewDrawerComponent.prototype.bindDragEvents = function () {
        var _this = this;
        if (!this.viewDrawer) {
            return;
        }
        // Clear out previous handlers
        this.unbindDragEvents();
        var element = this.viewDrawer.nativeElement;
        this.mc = new Hammer(element);
        this.mc.get('pan').set({ direction: Hammer.DIRECTION_VERTICAL, threshold: 5 });
        this.mc.on('panstart', function () {
            _this.handlePanStart();
        });
        this.mc.on('pan', function (event) {
            _this.handlePan(event);
        });
        this.mc.on('panend', function (event) {
            _this.updateStops(); // Ensure the scroll velocity always works properly
            _this.handlePanEnd(event);
        });
    };
    ViewDrawerComponent.prototype.unbindDragEvents = function () {
        if (this.mc) {
            this.mc.destroy();
        }
    };
    ViewDrawerComponent.prototype.handlePanStart = function () {
        this.drawerY = this.getDrawerY();
    };
    ViewDrawerComponent.prototype.handlePan = function (event) {
        this.flyTo(this.drawerY + event.deltaY);
    };
    ViewDrawerComponent.prototype.handlePanEnd = function (event) {
        this.moveDrawerWithVelocity(event.velocityY);
    };
    ViewDrawerComponent.prototype.handleNextChapter = function () {
        this.viewService.nextChapter();
    };
    ViewDrawerComponent.prototype.handlePreviousChapter = function () {
        this.viewService.previousChapter();
    };
    ViewDrawerComponent.prototype.handleToggleClick = function () {
        this.toggleDrawer();
    };
    // ---------- Utility ----------
    ViewDrawerComponent.prototype.toggleDrawer = function (open) {
        if (open != null) {
            var breakpoint = open ? this.breakpoints.halfDrawer : this.breakpoints.minimumDrawer;
            var velocity = open ? -toggleSpeed : toggleSpeed;
            this.moveDrawerToBreakpoint(breakpoint, velocity);
        }
        else {
            var breakpoint = this.fullyOpen ? this.breakpoints.minimumDrawer : this.breakpoints.halfDrawer;
            var velocity = breakpoint.point < this.getDrawerY() ? -toggleSpeed : toggleSpeed;
            this.moveDrawerToBreakpoint(breakpoint, velocity);
        }
    };
    ViewDrawerComponent.prototype.moveDrawerWithVelocity = function (velocityY) {
        var drawerY = this.getDrawerY();
        // If drawer is in motion, get next on it's path. Otherwise calculate nearest. 
        var breakpoint = (Math.abs(velocityY) > triggerVelocityThreshold) ?
            this.getNextBreakpoint(drawerY, velocityY) :
            this.getNearestBreakpoint(drawerY);
        // Ensure a velocity when there is none
        if (breakpoint != null && Math.abs(velocityY) < snapVelocity && !this.fullyOpen) {
            var direction = breakpoint.point < drawerY ? -1 : 1;
            velocityY = snapVelocity * direction;
        }
        // Move drawer to chosen breakpoint.
        if (breakpoint != null) {
            this.moveDrawerToBreakpoint(breakpoint, velocityY);
        }
    };
    ViewDrawerComponent.prototype.getNextBreakpoint = function (drawerY, velocity) {
        var e_1, _a, e_2, _b;
        var breakpoints = Object.values(this.breakpoints);
        var absoluteVelocity = Math.abs(velocity);
        if (velocity < 0) {
            try {
                for (var breakpoints_1 = __values(breakpoints), breakpoints_1_1 = breakpoints_1.next(); !breakpoints_1_1.done; breakpoints_1_1 = breakpoints_1.next()) {
                    var breakpoint = breakpoints_1_1.value;
                    if (breakpoint.point < drawerY && absoluteVelocity < breakpoint.velocity) {
                        return breakpoint;
                    }
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (breakpoints_1_1 && !breakpoints_1_1.done && (_a = breakpoints_1.return)) _a.call(breakpoints_1);
                }
                finally { if (e_1) throw e_1.error; }
            }
        }
        else {
            breakpoints = breakpoints.reverse();
            try {
                for (var breakpoints_2 = __values(breakpoints), breakpoints_2_1 = breakpoints_2.next(); !breakpoints_2_1.done; breakpoints_2_1 = breakpoints_2.next()) {
                    var breakpoint = breakpoints_2_1.value;
                    if (breakpoint.point > drawerY && absoluteVelocity < breakpoint.velocity) {
                        return breakpoint;
                    }
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (breakpoints_2_1 && !breakpoints_2_1.done && (_b = breakpoints_2.return)) _b.call(breakpoints_2);
                }
                finally { if (e_2) throw e_2.error; }
            }
        }
    };
    ViewDrawerComponent.prototype.getNearestBreakpoint = function (drawerY, breakpoints) {
        var nearestBreakpoint;
        var minimumDiff = Infinity;
        if (breakpoints == null) {
            breakpoints = Object.values(this.breakpoints);
        }
        breakpoints
            .forEach(function (breakpoint) {
            var diff = Math.abs(Math.abs(breakpoint.point) - Math.abs(drawerY));
            if (diff < minimumDiff) {
                minimumDiff = diff;
                nearestBreakpoint = breakpoint;
            }
        });
        return nearestBreakpoint;
    };
    ViewDrawerComponent.prototype.moveDrawerToBreakpoint = function (breakpoint, velocityY) {
        var _this = this;
        var distance = velocityY * velocityMultipler;
        requestAnimationFrame(function () {
            var drawerY = _this.getDrawerY();
            // Drawer not active, do not keep attempting to move it as this will cause
            // an infinite loop
            if (typeof drawerY === 'undefined') {
                return;
            }
            // Check if drawer should snap to breakpoint
            var shouldSnapDown = velocityY < 0 && (drawerY + distance) <= breakpoint.point;
            var shouldSnapUp = velocityY > 0 && (drawerY + distance) >= breakpoint.point;
            if (shouldSnapDown || shouldSnapUp) {
                _this.flyTo(breakpoint.point);
                // Otherwise, keep moving
            }
            else {
                // Apply friction
                if (_this.fullyOpen) {
                    velocityY = velocityY * friction;
                }
                var targetY = drawerY + (velocityY * velocityMultipler);
                _this.flyTo(targetY);
                if (Math.abs(velocityY) > 0.05) {
                    _this.moveDrawerToBreakpoint(breakpoint, velocityY);
                }
            }
        });
    };
    ViewDrawerComponent.prototype.flyTo = function (y) {
        var element = this.viewDrawer.nativeElement;
        // The logic of less and greater may seem reversed, but that's because the drawer position
        // is being translated with a negative y value. The lower the y value, the higher on screen
        // the drawer is positioned. 
        // drawerMax is calculated dynamically because the internal content of drawer can change size at any time.
        var drawerMax = -this.viewDrawer.nativeElement.offsetHeight;
        if (y < drawerMax) {
            y = drawerMax;
        }
        if (y > this.breakpoints.minimumDrawer.point) {
            y = this.breakpoints.minimumDrawer.point;
        }
        var transform = "translate3d(0, " + y + "px, 0)";
        this.renderer.setStyle(element, 'transform', transform);
        // Hide pagination when not at default position
        this.ghostPagination = (y !== this.breakpoints.minimumDrawer.point);
        // Check position
        this.isDrawerAtMinimum();
        this.offsetYChange.emit(y);
    };
    ViewDrawerComponent.prototype.getDrawerY = function () {
        if (this.viewDrawer) {
            var transform = this.viewService.getComputedTransform(this.viewDrawer.nativeElement);
            return transform ? transform.y : null;
        }
        else {
            return null;
        }
    };
    ViewDrawerComponent.prototype.isDrawerAtMinimum = function () {
        var y = this.getDrawerY();
        this.atMinimum = (y === this.breakpoints.minimumDrawer.point);
        this.changeDetectorRef.markForCheck();
    };
    __decorate([
        Select(ViewState.getCanTouch),
        __metadata("design:type", Observable)
    ], ViewDrawerComponent.prototype, "canTouch$", void 0);
    __decorate([
        Select(ViewState.getLayout),
        __metadata("design:type", Observable)
    ], ViewDrawerComponent.prototype, "layout$", void 0);
    __decorate([
        Select(StoryChaptersState.getSelectedChapterIndex),
        __metadata("design:type", Observable)
    ], ViewDrawerComponent.prototype, "chapterIndex$", void 0);
    __decorate([
        Select(StoryChaptersState.getChapterCount),
        __metadata("design:type", Observable)
    ], ViewDrawerComponent.prototype, "chapterCount$", void 0);
    return ViewDrawerComponent;
}());
export { ViewDrawerComponent };
